/**
 * Design System Tokens for TypeScript
 *
 * These values mirror the CSS variables in design-system.css.
 * Use these for inline styles; prefer Tailwind classes (z-layout, z-ant-dropdown, etc.) when possible.
 *
 * CSS variables remain the source of truth - keep these in sync when updating design-system.css.
 */

/**
 * Z-Index tokens - semantic layering system
 *
 * This system is designed to integrate with Ant Design's z-index hierarchy.
 * See: https://github.com/ant-design/ant-design/blob/master/components/_util/hooks/useZIndex.ts
 *
 * THREE-LAYER ARCHITECTURE:
 *
 * LAYOUT LAYER (0-999) - Page structure, below all popups
 * ├── negative: -1        → Decorative backgrounds
 * ├── base: 0             → Normal document flow
 * ├── elevated: 10        → Internal stacking within containers
 * └── layout: 100         → Fixed navbar, sidebar, toolbar
 *
 * ANT DESIGN LAYER (1000-2100) - For Ant components & matching custom components
 * ├── antPopupBase: 1000  → Modal, Drawer base
 * ├── antPopover: 1030    → Popover, info panels
 * ├── antDropdown: 1050   → Select, DatePicker, Dropdown popups
 * ├── antTooltip: 1070    → Tooltip hints
 * └── antMax: 2100        → Maximum Ant stacking (nested modals, etc.)
 *
 * CUSTOM OVERLAY LAYER (2200+) - Must appear above ALL Ant components
 * ├── customPopover: 2200 → SchedulerAssistant, special floating panels
 * ├── customToast: 2300   → Notification, alerts
 * └── customTooltip: 2400 → Custom tooltips that must never be hidden
 */
export const zIndex = {
  // ============================================
  // LAYOUT LAYER (0-999)
  // For page structure elements below floating UI
  // ============================================
  negative: -1, // Below content - decorative backgrounds
  base: 0, // Normal document flow
  elevated: 10, // Internal stacking within containers
  layout: 100, // Fixed navbar, sidebar, toolbar

  // ============================================
  // ANT DESIGN LAYER (1000-2100)
  // Mirror Ant's internal tiers for custom components
  // that need to integrate with Ant's stacking
  // ============================================
  antPopupBase: 1000, // Base for all Ant floating UI (Modal, Drawer, etc.)
  antPopover: 1030, // Popover (+30) - hover cards, info panels
  antDropdown: 1050, // Dropdown/Select/DatePicker (+50) - form controls
  antTooltip: 1070, // Tooltip (+70) - hints, labels
  antMax: 2100, // Max Ant z-index (base + CONTAINER_MAX_OFFSET + children)

  // ============================================
  // CUSTOM OVERLAY LAYER (2200+)
  // For our custom floating components that must
  // appear above all Ant components
  // ============================================
  customPopover: 2200, // SchedulerAssistant, custom floating panels
  customToast: 2300, // Notification, custom alerts
  customTooltip: 2400, // Custom tooltips that must never be occluded
} as const;

export type ZIndexTier = keyof typeof zIndex;
